/**
 * Audit Record Service – QMS Phase 3
 * CRUD for audit_records linked to Standards or Protocols.
 * PRD §5.2 Requirement 10.1
 */

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import auditService from './auditService';

const COLLECTION = 'audit_records';

const AUDIT_TYPES = ['internal', 'anmcs', 'external'];
const RESULTS = ['pass', 'fail', 'conditional', 'pass_with_recommendations'];

function omitUndefined(obj) {
  if (obj == null || typeof obj !== 'object') return obj;
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined) out[k] = v;
  }
  return out;
}

export class AuditRecordService {
  constructor() {
    this.collectionRef = collection(db, COLLECTION);
  }

  buildPayload(data, userId, now) {
    const base = {
      organizationId: data.organizationId ?? null,
      processId: data.processId ?? null,
      processCode: data.processCode ?? null,
      processName: data.processName ?? null,
      protocolId: data.protocolId ?? null,
      protocolName: data.protocolName ?? null,
      auditDate: data.auditDate ? Timestamp.fromDate(new Date(data.auditDate)) : now,
      auditorName: data.auditorName || '',
      auditType: data.auditType || 'internal',
      scope: data.scope || '',
      result: data.result || 'pass',
      findings: data.findings || '',
      correctiveActionsRequired: data.correctiveActionsRequired || '',
      nextAuditDue: data.nextAuditDue ? Timestamp.fromDate(new Date(data.nextAuditDue)) : null,
      evidenceRef: data.evidenceRef || null,
      notes: data.notes || '',
      createdAt: now,
      createdBy: userId,
      updatedAt: now,
      updatedBy: userId
    };
    return omitUndefined(base);
  }

  async create(data, userId) {
    const now = Timestamp.now();
    const payload = this.buildPayload(data, userId, now);
    const ref = await addDoc(this.collectionRef, payload);
    try {
      auditService.logCreate('audit_records', ref.id, data.scope?.slice(0, 50) || `${data.auditType} audit`, {
        auditType: data.auditType,
        result: data.result
      });
    } catch (e) {
      console.warn('Audit log failed:', e);
    }
    return ref.id;
  }

  async get(recordId) {
    const ref = doc(this.collectionRef, recordId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    const d = snap.data();
    return {
      id: snap.id,
      ...d,
      auditDate: d.auditDate?.toDate?.() ?? d.auditDate,
      nextAuditDue: d.nextAuditDue?.toDate?.() ?? d.nextAuditDue
    };
  }

  async getByOrganization(organizationId, options = {}) {
    const { processId, protocolId, limit = 100 } = options;
    const org = organizationId ?? null;
    const constraints = [];
    if (org != null) constraints.push(where('organizationId', '==', org));
    if (processId) constraints.push(where('processId', '==', processId));
    else if (protocolId) constraints.push(where('protocolId', '==', protocolId));
    constraints.push(orderBy('auditDate', 'desc'));
    const q = query(this.collectionRef, ...constraints);
    const snapshot = await getDocs(q);
    return snapshot.docs.slice(0, limit).map((d) => {
      const data = d.data();
      return {
        id: d.id,
        ...data,
        auditDate: data.auditDate?.toDate?.() ?? data.auditDate,
        nextAuditDue: data.nextAuditDue?.toDate?.() ?? data.nextAuditDue
      };
    });
  }

  async getUpcoming(organizationId, limit = 20) {
    const records = await this.getByOrganization(organizationId, { limit: 500 });
    const now = new Date();
    return records
      .filter((r) => r.nextAuditDue && new Date(r.nextAuditDue) >= now)
      .sort((a, b) => new Date(a.nextAuditDue) - new Date(b.nextAuditDue))
      .slice(0, limit);
  }

  async getOverdue(organizationId, limit = 20) {
    const records = await this.getByOrganization(organizationId, { limit: 500 });
    const now = new Date();
    return records
      .filter((r) => r.nextAuditDue && new Date(r.nextAuditDue) < now)
      .sort((a, b) => new Date(a.nextAuditDue) - new Date(b.nextAuditDue))
      .slice(0, limit);
  }

  async update(recordId, updates, userId) {
    const ref = doc(this.collectionRef, recordId);
    const patch = omitUndefined({
      ...updates,
      updatedAt: Timestamp.now(),
      updatedBy: userId
    });
    if (updates.auditDate) patch.auditDate = Timestamp.fromDate(new Date(updates.auditDate));
    if (updates.nextAuditDue) patch.nextAuditDue = Timestamp.fromDate(new Date(updates.nextAuditDue));
    await updateDoc(ref, patch);
    try {
      auditService.logUpdate('audit_records', recordId, updates.scope?.slice(0, 50) || recordId, {
        changedFields: Object.keys(updates)
      });
    } catch (e) {
      console.warn('Audit log failed:', e);
    }
  }

  async delete(recordId) {
    const ref = doc(this.collectionRef, recordId);
    const current = await this.get(recordId);
    await deleteDoc(ref);
    try {
      auditService.logDelete('audit_records', recordId, current?.scope?.slice(0, 50) || recordId, {});
    } catch (e) {
      console.warn('Audit log failed:', e);
    }
  }
}

export const auditRecordService = new AuditRecordService();
export { AUDIT_TYPES, RESULTS };
